/**
 * Shared helpers for org / program / user member attributes.
 * Keeps GET unwrap, catalog normalization, grp selection merge, and PUT payload shape in one place.
 */

export type MemberAttributeValueRow = {
  _id: string;
  value: string;
  selected?: boolean;
};

export type MemberAttributeGroupState = {
  _id: string;
  name: string;
  values: MemberAttributeValueRow[];
};

export type GroupMemberAttributesPutItem = {
  _id: string;
  name: string;
  values: Array<{ _id: string; value: string; selected: boolean }>;
};

export type MemberAttributeGroupCatalog = {
  _id: string;
  name: string;
  values: Array<{ _id: string; value: string }>;
};

/**
 * One org attribute list's contribution to a program's audience.
 * Mirrors the BE `IAudienceRule` (shared/model/groupsModel.ts).
 *
 * Matching is AND across rules, `matchType` within a rule:
 *   any → member holds at least one of `valueIds`
 *   all → member holds every one of `valueIds`
 *
 * A list the admin left empty must NOT be sent as a rule — the BE rejects a
 * rule with 0 valueIds, and semantically an empty list simply doesn't
 * participate in matching.
 */
export type AudienceMatchType = 'any' | 'all';

export interface AudienceRule {
  listId: string;
  listName: string;
  matchType: AudienceMatchType;
  valueIds: string[];
}

/** Strip empty rules so we never send a valueless rule to the BE. */
export function participatingAudienceRules(rules: AudienceRule[] | null | undefined): AudienceRule[] {
  if (!Array.isArray(rules)) return [];
  return rules.filter((r) => r && Array.isArray(r.valueIds) && r.valueIds.length > 0);
}

/** True when the program targets an audience at all. */
export function hasAudienceTargeting(rules: AudienceRule[] | null | undefined): boolean {
  return participatingAudienceRules(rules).length > 0;
}

/** Flatten to value ids — for label resolution and the legacy mirror field. */
export function flattenAudienceRules(rules: AudienceRule[] | null | undefined): string[] {
  const seen = new Set<string>();
  for (const rule of participatingAudienceRules(rules)) {
    for (const id of rule.valueIds) seen.add(id);
  }
  return Array.from(seen);
}

/**
 * Backward compatibility: turn a legacy flat audience (`string[]` of value ids)
 * into one "any" rule per owning list, using the org catalog. Used to hydrate
 * the new UI from programs that haven't been migrated/re-saved yet.
 * Ids that resolve to no list are dropped (the value was deleted org-side).
 */
export function audienceRulesFromFlat(
  flatValueIds: string[] | null | undefined,
  catalog: MemberAttributeGroupCatalog[]
): AudienceRule[] {
  if (!Array.isArray(flatValueIds) || flatValueIds.length === 0) return [];

  const ownerByValueId = new Map<string, MemberAttributeGroupCatalog>();
  for (const list of catalog) {
    for (const value of list.values) {
      if (value._id) ownerByValueId.set(String(value._id), list);
    }
  }

  const byListId = new Map<string, AudienceRule>();
  for (const rawId of flatValueIds) {
    const id = String(rawId);
    const list = ownerByValueId.get(id);
    if (!list) continue;
    const existing = byListId.get(list._id);
    if (existing) {
      if (!existing.valueIds.includes(id)) existing.valueIds.push(id);
    } else {
      byListId.set(list._id, {
        listId: list._id,
        listName: list.name,
        matchType: 'any',
        valueIds: [id]
      });
    }
  }
  return Array.from(byListId.values());
}

/**
 * Apply per-list Any/All choices (keyed by list id) to rules built from a flat
 * selection — e.g. the Elliott chat's picks. Lists without a choice keep their
 * matchType ("any" from audienceRulesFromFlat).
 */
export function applyAudienceMatchTypes(
  rules: AudienceRule[],
  matchByListId: Record<string, AudienceMatchType> | null | undefined
): AudienceRule[] {
  return rules.map((rule) => ({ ...rule, matchType: matchByListId?.[rule.listId] ?? rule.matchType }));
}

export type GroupMemberAttributesApiPayload = {
  orgMemberAttributes?: unknown;
  grpMemberAttributes?: unknown;
  /** legacy / alternate response keys */
  attributes?: unknown;
  memberAttributes?: unknown;
};

const OBJECT_ID_RE = /^[a-f0-9]{24}$/i;

export function isMongoObjectIdString(v: string): boolean {
  return OBJECT_ID_RE.test(v);
}

const HIDDEN_MEMBER_ATTRIBUTE_NAMES = new Set([
  'new business development',
  'account management',
  'logistics',
  'procurement',
  'artwork'
]);

function normalizeAttrName(name: unknown): string {
  return String(name ?? '').trim().toLowerCase();
}

export function isHiddenMemberAttributeName(name: unknown): boolean {
  return HIDDEN_MEMBER_ATTRIBUTE_NAMES.has(normalizeAttrName(name));
}

/**
 * Strip values (and groups) whose label matches the org-level hide list.
 * Drops groups left empty after value filtering.
 */
export function filterHiddenMemberAttributes<
  G extends { name?: unknown; values?: Array<{ value?: unknown } & Record<string, unknown>> }
>(groups: G[] | undefined | null): G[] {
  if (!Array.isArray(groups)) return [];
  return groups
    .filter((g) => !isHiddenMemberAttributeName(g?.name))
    .map((g) => ({
      ...g,
      values: (Array.isArray(g?.values) ? g.values : []).filter(
        (v) => !isHiddenMemberAttributeName(v?.value)
      )
    }))
    .filter((g) => Array.isArray(g.values) && g.values.length > 0);
}

/** Every value id an (already hidden-filtered) catalog holds. */
export function catalogValueIds<
  G extends { values?: Array<{ _id?: unknown }> }
>(groups: G[] | undefined | null): string[] {
  if (!Array.isArray(groups)) return [];
  const ids: string[] = [];
  for (const g of groups) {
    for (const v of Array.isArray(g?.values) ? g.values : []) {
      if (typeof v?._id === 'string') ids.push(v._id);
    }
  }
  return ids;
}

/**
 * Does the member still owe an answer for this catalog?
 *
 * `ackIds` is what the backend recorded the last time they saved: the catalog
 * as it stood in front of them. They owe an answer again only once the catalog
 * holds a value they were never shown.
 *
 * Deliberately NOT "the member holds no value from some list". A list like
 * "Management: Managers | SLT" applies to almost nobody, so that reading made
 * the prompt unanswerable — it reappeared on every single sign-in for anyone
 * who is neither. Selecting nothing from a list is a complete answer.
 *
 * `undefined` ack (every member predating this field) returns false: they are
 * prompted by the backend flag or by holding no attributes at all, not by this.
 */
export function hasUnacknowledgedAttributes<
  G extends { values?: Array<{ _id?: unknown }> }
>(orgAttrs: G[] | undefined | null, ackIds: string[] | undefined | null): boolean {
  if (!Array.isArray(ackIds)) return false;
  const acked = new Set(ackIds);
  return catalogValueIds(orgAttrs).some((id) => !acked.has(id));
}

/**
 * Org attribute lists the member has not answered yet.
 *
 * A list counts as answered when the member either holds at least one of its
 * values, or explicitly declined it ("None of these apply to me", recorded by
 * the BE as `memberAttributesDeclinedLists`). Anything else is unanswered.
 *
 * This is what makes the requirement answerable. A list like "Management:
 * Managers | SLT" applies to almost nobody, so demanding a VALUE from every
 * list would be an unanswerable question — the exact bug the ack fix above
 * removed. Declining is a first-class answer, which is why it needs its own
 * field and cannot be inferred from an empty selection.
 *
 * `orgAttrs` must already be hidden-filtered: a list we never render can never
 * be answered, so it must never be required.
 */
export function unansweredAttributeListIds<
  G extends { _id?: unknown; values?: Array<{ _id?: unknown }> }
>(
  orgAttrs: G[] | undefined | null,
  selectedValueIds: string[] | undefined | null,
  declinedListIds: string[] | undefined | null
): string[] {
  if (!Array.isArray(orgAttrs)) return [];
  const selected = new Set((selectedValueIds ?? []).map(String));
  const declined = new Set((declinedListIds ?? []).map(String));
  const unanswered: string[] = [];
  for (const list of orgAttrs) {
    const listId = String(list?._id ?? '');
    if (!listId) continue;
    if (declined.has(listId)) continue;
    const values = Array.isArray(list?.values) ? list.values : [];
    // A list with no renderable values is not answerable, so not required.
    if (values.length === 0) continue;
    const hasValue = values.some((v) => v?._id && selected.has(String(v._id)));
    if (!hasValue) unanswered.push(listId);
  }
  return unanswered;
}

/** Unify axios and double-wrapped API bodies: `{ data: { data: T } }` → `T` */
export function unwrapEnvelope<T>(input: unknown): T {
  if (input == null) return input as T;
  const anyIn = input as { data?: { data?: unknown } };
  return (anyIn?.data?.data ?? anyIn?.data ?? anyIn) as T;
}

/**
 * Normalize org catalog rows (attribute groups with value ids).
 * Filters corrupt rows (ObjectId as name/value).
 */
export function normalizeOrgCatalog(
  orgMemberAttributes: unknown,
  profileFallback?: unknown
): MemberAttributeGroupCatalog[] {
  let raw: unknown[] = Array.isArray(orgMemberAttributes) ? orgMemberAttributes : [];
  if (
    raw.length === 0 &&
    Array.isArray(profileFallback) &&
    (profileFallback as unknown[]).length > 0
  ) {
    raw = profileFallback as unknown[];
  }

  return raw
    .filter((g: unknown) => Boolean(g && typeof g === 'object' && typeof g !== 'string'))
    .map((groupUnknown) => {
      const group = groupUnknown as Record<string, unknown>;
      return {
        _id: String(group?._id ?? group?.id ?? ''),
        name: String(
          group?.name ?? group?.title ?? group?.label ?? group?.attributeName ?? group?.key ?? ''
        ),
        values: (Array.isArray(group?.values) ? group.values : [])
          .map((option: unknown) => {
            if (typeof option === 'string') {
              if (isMongoObjectIdString(option)) return null;
              return { _id: '', value: option };
            }
            const o = option as Record<string, unknown>;
            const value = String(o?.value ?? o?.label ?? o?.name ?? '');
            if (!value || isMongoObjectIdString(value)) return null;
            return { _id: String(o?._id ?? o?.id ?? ''), value };
          })
          .filter(Boolean) as Array<{ _id: string; value: string }>
      };
    })
    .filter((g) => g.name.length > 0 && !isCorruptedAttributeGroupName(g.name))
    .filter((g) => !isHiddenMemberAttributeName(g.name))
    .map((g) => ({
      ...g,
      values: g.values.filter((v) => !isHiddenMemberAttributeName(v.value))
    }))
    .filter((g) => g.values.length > 0);
}

const CORRUPTED_NAMES = new Set(['grpmemberattributes', 'orgmemberattributes', 'memberattributes']);

function isCorruptedAttributeGroupName(name: string): boolean {
  return isMongoObjectIdString(name) || CORRUPTED_NAMES.has(name.toLowerCase().replace(/[_\- ]/g, ''));
}

/**
 * Merge program/group selection: `grpMemberAttributes` is the source of truth for `selected`
 * on each value row (backend does not send per-value `selected` on GET).
 */
export function mergeGrpSelection(
  orgCatalog: MemberAttributeGroupCatalog[],
  grpMemberAttributes: string[]
): MemberAttributeGroupState[] {
  const selectedSet = new Set((grpMemberAttributes || []).map(String));
  return orgCatalog.map((group) => ({
    _id: group._id,
    name: group.name,
    values: group.values.map((v) => ({
      _id: v._id,
      value: v.value,
      selected: Boolean(v._id && selectedSet.has(String(v._id)))
    }))
  }));
}

/** Canonical PUT body for `PUT /groups/:grpId/memberAttributes` */
export function buildGroupPutPayload(
  groups: Array<{
    _id?: string;
    name: string;
    values: Array<{ _id?: string; value: string; selected?: boolean }>;
  }>
): GroupMemberAttributesPutItem[] {
  return groups.map((g) => ({
    _id: g._id ?? '',
    name: g.name,
    values: (g.values || []).map((v) => ({
      _id: v._id ?? '',
      value: v.value,
      selected: v.selected === true
    }))
  }));
}

/** Map value id → display info for profile chips */
export function resolveLabels(
  orgCatalog: MemberAttributeGroupCatalog[]
): Map<string, { attributeName: string; value: string }> {
  const map = new Map<string, { attributeName: string; value: string }>();
  for (const attr of orgCatalog) {
    for (const v of attr.values || []) {
      if (v._id) map.set(String(v._id), { attributeName: attr.name, value: v.value });
    }
  }
  return map;
}

/**
 * One org attribute list as the member's own profile renders it:
 * the list name plus the human labels of the values that member holds.
 */
export interface MemberAttributeProfileRow {
  listId: string;
  listName: string;
  /** Labels the member holds in this list, in catalog order. May be empty. */
  values: string[];
  /**
   * The member explicitly answered "none of these apply to me" for this list.
   * Always false until the backend ships `users.memberAttributesDeclinedLists`;
   * the caller can then pass those ids straight through and an empty row reads
   * "None" instead of "not answered yet".
   */
  declined: boolean;
}

/**
 * Resolve a member's stored value ids (`users.memberAttributes`) against the
 * org catalog into one row per list, for display.
 *
 * Why rows and not a flat chip list: the ids carry no list, so "Design" alone
 * is ambiguous (Department: Design vs Office: Design Studio). The profile has
 * to say which list each answer belongs to.
 *
 * Ids that resolve to nothing are dropped, never echoed. An id survives on the
 * user doc after the org deletes that value, and hidden values are filtered out
 * of the catalog on purpose — in both cases rendering the id would put a raw
 * ObjectId on the member's profile.
 *
 * A list the member holds no value in still gets a row (with `values: []`), so
 * the profile can show what the org asked and what is still unanswered.
 *
 * `orgCatalog` must already be normalized (see `normalizeOrgCatalog`, which
 * also applies the hidden-attribute filter).
 */
export function resolveMemberAttributeRows(
  orgCatalog: MemberAttributeGroupCatalog[],
  selectedValueIds: string[] | null | undefined,
  declinedListIds?: string[] | null
): MemberAttributeProfileRow[] {
  const selected = new Set((selectedValueIds ?? []).map(String));
  const declined = new Set((declinedListIds ?? []).map(String));
  return (Array.isArray(orgCatalog) ? orgCatalog : []).map((list) => ({
    listId: String(list?._id ?? ''),
    listName: String(list?.name ?? ''),
    values: (Array.isArray(list?.values) ? list.values : [])
      .filter((v) => v?._id && selected.has(String(v._id)))
      .map((v) => v.value),
    declined: declined.has(String(list?._id ?? ''))
  }));
}

export function parseGroupMemberAttributesResponse(
  rawResponse: unknown,
  profileOrgFallback?: unknown
): { orgCatalog: MemberAttributeGroupCatalog[]; grpIds: string[] } {
  const payload = unwrapEnvelope<GroupMemberAttributesApiPayload>(rawResponse);
  const orgCatalog = normalizeOrgCatalog(payload?.orgMemberAttributes, profileOrgFallback);
  const grpIds = Array.isArray(payload?.grpMemberAttributes)
    ? (payload.grpMemberAttributes as unknown[]).map((x) => String(x))
    : [];
  return { orgCatalog, grpIds };
}

/**
 * True if any id that was in `initialGrpValueIds` is no longer selected in the current UI state.
 * Used to warn that the backend currently ignores removals from the program/org root group.
 */
export function detectAttemptedGrpDeselection(
  initialGrpValueIds: Set<string>,
  groups: MemberAttributeGroupState[]
): boolean {
  const currentlySelected = new Set<string>();
  for (const g of groups) {
    for (const v of g.values || []) {
      if (v.selected === true && v._id) currentlySelected.add(String(v._id));
    }
  }
  for (const id of initialGrpValueIds) {
    if (id && !currentlySelected.has(id)) return true;
  }
  return false;
}

/** ProgramUsers keeps selection in `autoInviteAttributes` keys (`name||value`), not only on `values[].selected`. */
export function detectAttemptedGrpDeselectionFromProgramUi(
  initialGrpValueIds: Set<string>,
  groups: Array<{ name: string; values: Array<{ _id?: string; value: string }> }>,
  autoInviteAll: boolean,
  selectedKeys: Set<string>
): boolean {
  const currentIds = new Set<string>();
  for (const g of groups) {
    for (const v of g.values || []) {
      const id = v._id ? String(v._id) : '';
      if (!id) continue;
      if (autoInviteAll || selectedKeys.has(`${g.name}||${v.value}`)) {
        currentIds.add(id);
      }
    }
  }
  for (const id of initialGrpValueIds) {
    if (id && !currentIds.has(id)) return true;
  }
  return false;
}
